import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Copy,
  Link2,
  Loader,
  Mail,
  Share2,
  Trash2,
  UserMinus,
  Users,
} from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { useDesign } from "../lib/store";
import { useLibrary } from "../lib/library";
import { useAuth, hasAccount } from "../lib/auth";
import { useSignInPromptShown } from "../lib/useSignInPromptShown";
import {
  buildInviteLink,
  callCreateShareInvite,
  callCreateSharedRoom,
  collabLimitFromError,
  callDeleteSharedRoom,
  callRemoveShareMember,
  callRevokeShareInvite,
  shareInviteLink,
} from "../lib/sharedInvites";
import {
  fetchSharedRoom,
  subscribeInvitesForRoom,
  subscribeSharedRoom,
} from "../lib/sharedRoomsCloud";
import {
  shareInviteKind,
  type ShareInvite,
  type SharedRoomDoc,
  type ShareMember,
} from "../lib/sharedRoomTypes";
import { openRoom, exitSharedRoom } from "../lib/openRoom";
import { requestUpsell } from "../lib/upsellRequests";
import { colorForUid, initialsFromName } from "../lib/cursorColor";
import { cn } from "../lib/cn";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Owner-side share UI. Lets the owner of a saved room hand out an invite
 * link (anyone with it can join after Google sign-in), or lock an invite
 * to a specific email, see pending/accepted invites, and revoke or remove
 * members.
 *
 * The "active" room (i.e. what we're sharing) is whichever room the user
 * has loaded — either a private SavedRoom (`activeSavedRoomId`) or an
 * already-shared room they own (`activeSharedRoomId`). For private
 * rooms, opening the sheet bootstraps a fresh `sharedRooms/{id}` doc via
 * `createSharedRoom` on first invite — the user doesn't have to "promote"
 * explicitly.
 */
export function ShareSheet({ open, onClose }: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="Share this room" side="right">
      <Body onClose={onClose} />
    </Sheet>
  );
}

function Body({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const design = useDesign((s) => s.design);
  const activeSavedRoomId = useDesign((s) => s.activeSavedRoomId);
  const activeSharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const rooms = useLibrary((s) => s.rooms);

  const localRoom = useMemo(
    () => rooms.find((r) => r.id === activeSavedRoomId) ?? null,
    [rooms, activeSavedRoomId]
  );
  useSignInPromptShown("share", auth.status === "signed-out" || !hasAccount(auth.user));

  if (auth.status === "loading") {
    return <Loading />;
  }
  if (auth.status !== "signed-in" || !hasAccount(auth.user)) {
    return (
      <PromptCard
        icon={<Mail className="size-4" />}
        title="Sign in to share"
        body="Sharing requires a Google account so everyone in the room has a verified identity."
      />
    );
  }
  if (!auth.user) return <Loading />;

  if (!activeSavedRoomId && !activeSharedRoomId) {
    return (
      <PromptCard
        icon={<AlertCircle className="size-4" />}
        title="Save the room first"
        body="Open the Rooms sheet, give this layout a name and tap Save. Then come back to share it."
      />
    );
  }

  return (
    <SignedInBody
      ownerUid={auth.user.uid}
      ownerName={auth.user.displayName}
      activeSavedRoomId={activeSavedRoomId}
      activeSharedRoomId={activeSharedRoomId}
      localRoomName={localRoom?.name ?? design.room.name ?? "Untitled room"}
      onClose={onClose}
    />
  );
}

interface SignedInBodyProps {
  ownerUid: string;
  ownerName: string | null;
  activeSavedRoomId: string | null;
  activeSharedRoomId: string | null;
  localRoomName: string;
  onClose: () => void;
}

function SignedInBody({
  ownerUid,
  ownerName,
  activeSavedRoomId,
  activeSharedRoomId,
  localRoomName,
  onClose,
}: SignedInBodyProps) {
  // The room we're operating on. Either the user is already in the
  // shared session (`activeSharedRoomId`), or they have a private saved
  // room that hasn't been promoted yet — in which case we'll
  // promote-on-first-invite and use its id.
  const roomId = activeSharedRoomId ?? activeSavedRoomId;
  const [sharedRoom, setSharedRoom] = useState<SharedRoomDoc | null>(null);
  const [invites, setInvites] = useState<ShareInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"" | "promoting" | "deleting">("");
  const [error, setError] = useState<string | null>(null);

  /** A free-tier limit is an upgrade moment, not an error. */
  function reportError(err: unknown) {
    if (collabLimitFromError(err)) {
      onClose();
      requestUpsell("collab_unlimited", "limit");
      return;
    }
    setError(asMessage(err));
  }

  // Resolve whether a sharedRoom doc exists for this id. If yes, use it.
  // If no (private library room not promoted yet), the doc is created
  // lazily when the user issues their first invite.
  useEffect(() => {
    if (!roomId) return;
    setLoading(true);
    setError(null);
    let active = true;
    void fetchSharedRoom(roomId).then((doc) => {
      if (!active) return;
      setSharedRoom(doc);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [roomId]);

  // Live updates while the sheet is open.
  useEffect(() => {
    if (!roomId || !sharedRoom) return;
    const unsubRoom = subscribeSharedRoom(
      roomId,
      (doc) => setSharedRoom(doc),
      (err) => setError(err.message)
    );
    const unsubInvites = subscribeInvitesForRoom(
      roomId,
      ownerUid,
      (next) => setInvites(next),
      (err) => setError(err.message)
    );
    return () => {
      unsubRoom();
      unsubInvites();
    };
  }, [roomId, sharedRoom, ownerUid]);

  async function ensureSharedRoom(): Promise<string> {
    if (sharedRoom) return sharedRoom.id;
    if (!roomId) throw new Error("No room is loaded.");
    setBusy("promoting");
    try {
      // Promote the local design into a sharedRoom. The Cloud Function
      // writes the sharedRoom doc + the owner's inbox pointer atomically.
      const design = useDesign.getState().design;
      await callCreateSharedRoom({
        roomId,
        name: localRoomName,
        design,
      });
      // Re-fetch to populate the local SharedRoom doc state for the
      // members/invites UI in this sheet.
      const doc = await fetchSharedRoom(roomId);
      if (!doc) throw new Error("Failed to load the new shared room.");
      setSharedRoom(doc);
      // Enter the freshly-promoted room — `openRoom` runs the same
      // path the owner would take if they opened it from any other
      // entry point: starts `startSharedRoomSync` (bidirectional
      // Firestore sync), sets `activeSharedRoomId`, and mirrors the
      // `?shared=` URL so the link is shareable.
      await openRoom(doc.id, { forceShared: true });
      return doc.id;
    } finally {
      setBusy("");
    }
  }

  async function handleSendInvite(email: string) {
    setError(null);
    try {
      const id = await ensureSharedRoom();
      const res = await callCreateShareInvite({
        kind: "email",
        roomId: id,
        granteeEmail: email,
      });
      const link = buildInviteLink(res.inviteId, { from: ownerName });
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        // Clipboard API may be unavailable (e.g. http://). Surface the
        // link below the form instead.
      }
    } catch (err) {
      reportError(err);
    }
  }

  /**
   * Hand out a link invite. Reuses the newest still-usable link so
   * tapping the button twice doesn't mint two invites; otherwise mints a
   * fresh one (promoting the room first if needed).
   */
  async function handleShareLink(): Promise<"web_share" | "copy" | null> {
    setError(null);
    try {
      const id = await ensureSharedRoom();
      const reusable = invites.find(
        (i) =>
          shareInviteKind(i) === "link" &&
          i.status === "pending" &&
          (i.expiresAt == null || i.expiresAt > Date.now())
      );
      const inviteId = reusable
        ? reusable.id
        : (await callCreateShareInvite({ kind: "link", roomId: id })).inviteId;
      const link = buildInviteLink(inviteId, { from: ownerName });
      const method = await shareInviteLink(link, {
        roomName: sharedRoom?.name ?? localRoomName,
        from: ownerName,
      });
      if (method === null && !("share" in navigator)) {
        window.prompt("Copy this invite link:", link);
      }
      return method;
    } catch (err) {
      reportError(err);
      return null;
    }
  }

  async function handleRevoke(inviteId: string) {
    setError(null);
    try {
      await callRevokeShareInvite({ inviteId });
    } catch (err) {
      reportError(err);
    }
  }

  async function handleRemoveMember(uid: string) {
    if (!sharedRoom) return;
    if (!confirm("Remove this collaborator from the shared room?")) return;
    setError(null);
    try {
      await callRemoveShareMember({ roomId: sharedRoom.id, uid });
    } catch (err) {
      reportError(err);
    }
  }

  async function handleStopSharing() {
    if (!sharedRoom) return;
    if (
      !confirm(
        `Stop sharing "${sharedRoom.name}" with everyone? This deletes the live shared session. Your private library copy is unaffected.`
      )
    ) {
      return;
    }
    setError(null);
    setBusy("deleting");
    try {
      await callDeleteSharedRoom({ roomId: sharedRoom.id });
      // Tear down the sync session and clear the `?shared=` URL — the
      // registry will eventually remove this room from the inbox too,
      // but doing it here keeps the UI from briefly showing a stale
      // multiplayer banner.
      exitSharedRoom();
      onClose();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy("");
    }
  }

  if (loading) return <Loading />;

  return (
    <div className="flex flex-col gap-5 pt-1">
      <Header
        roomName={sharedRoom?.name ?? localRoomName}
        memberCount={sharedRoom ? Object.keys(sharedRoom.members).length : 1}
      />

      <LinkInviteCard onShare={handleShareLink} disabled={busy !== ""} />

      <InviteForm onSubmit={handleSendInvite} disabled={busy !== ""} />

      {error && (
        <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5 text-caption text-rose-700 flex items-start gap-2">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {sharedRoom && (
        <>
          <PendingInvitesList invites={invites} onRevoke={handleRevoke} />
          <MembersList
            members={sharedRoom.members}
            ownerUid={sharedRoom.ownerUid}
            onRemove={handleRemoveMember}
          />
          <div className="border-t border-ink-100 pt-4">
            <Button
              variant="danger"
              onClick={handleStopSharing}
              disabled={busy !== ""}
              className="w-full"
            >
              <Trash2 className="size-4" />
              Stop sharing & delete shared session
            </Button>
            <div className="mt-2 text-caption text-ink-400">
              Removes everyone's access. Your private library copy stays.
            </div>
          </div>
        </>
      )}

      <div className="border-t border-ink-100 pt-4 text-caption text-ink-500 leading-relaxed space-y-1.5">
        <div>
          <strong className="text-ink-700">How sharing works.</strong> An
          invite link lets anyone who has it join after signing in with
          Google — it works a few times, then expires. An email invite is
          locked to one address. Once someone joins, you both edit at the
          same time and see each other's cursor and selection.
        </div>
        <div>
          <strong className="text-ink-700">Limitations.</strong> Undo
          reverts your last edit and can overwrite a collaborator if you
          both changed the same fields. Imported product catalogs stay
          per-user. Offline edits are queued and may conflict on reconnect.
        </div>
      </div>
    </div>
  );
}

function Header({
  roomName,
  memberCount,
}: {
  roomName: string;
  memberCount: number;
}) {
  return (
    <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 px-4 py-3">
      <div className="flex items-center gap-2 text-body-sm text-ink-600">
        <Users className="size-3.5 text-ink-400" />
        <span className="truncate">
          <span className="font-semibold text-ink-900">{roomName}</span> ·{" "}
          {memberCount === 1 ? "just you" : `${memberCount} members`}
        </span>
      </div>
    </div>
  );
}

/**
 * Primary share action. One tap mints (or reuses) a link invite and hands
 * it to the OS share sheet on phones, or the clipboard on desktop.
 */
function LinkInviteCard({
  onShare,
  disabled,
}: {
  onShare: () => Promise<"web_share" | "copy" | null>;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<"web_share" | "copy" | null>(null);
  const canWebShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function handle() {
    setBusy(true);
    setDone(null);
    try {
      const method = await onShare();
      setDone(method);
      if (method) setTimeout(() => setDone(null), 2500);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-white ring-1 ring-ink-200 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-caption font-medium text-ink-700">
        <Link2 className="size-3.5 text-ink-400" />
        Invite link
      </div>
      <Button
        variant="primary"
        onClick={() => void handle()}
        disabled={busy || disabled}
        className="w-full"
        data-testid="share-link-button"
      >
        {busy ? (
          <Loader className="size-4 animate-spin" />
        ) : done === "copy" ? (
          <>
            <Check className="size-4" /> Link copied
          </>
        ) : canWebShare ? (
          <>
            <Share2 className="size-4" /> Share invite link
          </>
        ) : (
          <>
            <Copy className="size-4" /> Copy invite link
          </>
        )}
      </Button>
      <div className="text-caption text-ink-400">
        Anyone with the link can join after signing in with Google. Works 5
        times, expires in 14 days.
      </div>
    </div>
  );
}

function InviteForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (email: string) => Promise<void>;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit() {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setSending(true);
    setSuccess(null);
    try {
      await onSubmit(trimmed);
      setSuccess(trimmed);
      setEmail("");
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start inline-flex items-center gap-1.5 text-caption font-medium text-ink-600 hover:text-ink-900"
        data-testid="share-email-toggle"
      >
        <Mail className="size-3.5" />
        Invite a specific email instead
        <ChevronDown className="size-3.5" />
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="text-caption font-medium text-ink-700">
        Invite a specific email
      </label>
      <div className="flex gap-2">
        <input
          type="email"
          inputMode="email"
          autoComplete="off"
          value={email}
          placeholder="collaborator@example.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSubmit();
          }}
          disabled={sending || disabled}
          className="flex-1 h-10 px-3 rounded-xl bg-white ring-1 ring-ink-200 text-sm text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40"
        />
        <Button
          variant="secondary"
          onClick={() => void handleSubmit()}
          disabled={sending || disabled || email.trim().length === 0}
        >
          {sending ? (
            <Loader className="size-4 animate-spin" />
          ) : (
            <>
              <Mail className="size-4" /> Invite
            </>
          )}
        </Button>
      </div>
      {success && (
        <div className="text-caption text-emerald-700">
          Invite sent to {success}. The link is on the clipboard.
        </div>
      )}
      <div className="text-caption text-ink-400">
        Only the exact Google-signed-in email can accept this invite.
      </div>
    </div>
  );
}

function PendingInvitesList({
  invites,
  onRevoke,
}: {
  invites: ShareInvite[];
  onRevoke: (inviteId: string) => Promise<void>;
}) {
  const pending = invites.filter((i) => i.status === "pending");
  if (pending.length === 0) return null;
  return (
    <div className="border-t border-ink-100 pt-4">
      <div className="text-caption font-medium text-ink-500 uppercase tracking-wide mb-2">
        Pending invites ({pending.length})
      </div>
      <ul className="flex flex-col gap-2">
        {pending.map((invite) => (
          <PendingInviteRow
            key={invite.id}
            invite={invite}
            onRevoke={() => onRevoke(invite.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function describeLinkInvite(invite: ShareInvite, now = Date.now()): string {
  const uses = invite.uses ?? 0;
  const max = invite.maxUses ?? 5;
  const parts = [`${uses} of ${max} used`];
  if (invite.expiresAt != null) {
    const days = Math.ceil((invite.expiresAt - now) / (24 * 60 * 60 * 1000));
    parts.push(days <= 0 ? "expired" : days === 1 ? "expires tomorrow" : `expires in ${days}d`);
  }
  return parts.join(" · ");
}

function PendingInviteRow({
  invite,
  onRevoke,
}: {
  invite: ShareInvite;
  onRevoke: () => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const isLink = shareInviteKind(invite) === "link";
  const link = useMemo(
    () => buildInviteLink(invite.id, { from: invite.ownerDisplayName }),
    [invite.id, invite.ownerDisplayName]
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this link:", link);
    }
  }

  return (
    <li className="flex items-center gap-3 p-2.5 rounded-2xl bg-white ring-1 ring-ink-200">
      <div
        className={cn(
          "size-9 rounded-full flex items-center justify-center font-semibold",
          isLink ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"
        )}
      >
        {isLink ? <Link2 className="size-4" /> : <Mail className="size-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-body-sm font-semibold text-ink-900 truncate">
          {isLink ? "Invite link" : invite.granteeEmail}
        </div>
        <div className="text-caption text-ink-500">
          {isLink ? describeLinkInvite(invite) : "Awaiting acceptance"}
        </div>
      </div>
      <button
        type="button"
        onClick={() => void copy()}
        title="Copy invite link"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 active:bg-ink-200 transition-colors"
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
      <button
        type="button"
        onClick={() => void onRevoke()}
        title="Revoke invite"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-rose-50 hover:text-rose-600 active:bg-rose-100 transition-colors"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}

function MembersList({
  members,
  ownerUid,
  onRemove,
}: {
  members: Record<string, ShareMember>;
  ownerUid: string;
  onRemove: (uid: string) => Promise<void>;
}) {
  const list = useMemo(() => {
    const arr = Object.entries(members).map(([uid, m]) => ({ uid, ...m }));
    // Owner first, then editors sorted by joinedAt.
    arr.sort((a, b) => {
      if (a.uid === ownerUid) return -1;
      if (b.uid === ownerUid) return 1;
      return a.joinedAt - b.joinedAt;
    });
    return arr;
  }, [members, ownerUid]);
  return (
    <div className="border-t border-ink-100 pt-4">
      <div className="text-caption font-medium text-ink-500 uppercase tracking-wide mb-2">
        Members ({list.length})
      </div>
      <ul className="flex flex-col gap-2">
        {list.map((m) => {
          const isOwner = m.uid === ownerUid;
          const color = colorForUid(m.uid);
          return (
            <li
              key={m.uid}
              className={cn(
                "flex items-center gap-3 p-2.5 rounded-2xl bg-white ring-1 ring-ink-200"
              )}
            >
              <Avatar
                photoURL={m.photoURL ?? null}
                displayName={m.displayName ?? m.email ?? "Guest"}
                color={color}
              />
              <div className="min-w-0 flex-1">
                <div className="text-body-sm font-semibold text-ink-900 truncate">
                  {m.displayName || m.email || "Guest"}
                  {isOwner && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 rounded-full bg-ink-900 text-white text-label font-semibold">
                      owner
                    </span>
                  )}
                </div>
                <div className="text-caption text-ink-500 truncate">
                  {m.email}
                </div>
              </div>
              {!isOwner && (
                <button
                  type="button"
                  onClick={() => void onRemove(m.uid)}
                  title="Remove member"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-rose-50 hover:text-rose-600 active:bg-rose-100 transition-colors"
                >
                  <UserMinus className="size-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Avatar({
  photoURL,
  displayName,
  color,
}: {
  photoURL: string | null;
  displayName: string | null;
  color: string;
}) {
  if (photoURL) {
    return (
      <div
        className="size-9 rounded-full bg-ink-200 ring-2 overflow-hidden"
        style={{ borderColor: color }}
      >
        <img
          src={photoURL}
          alt=""
          referrerPolicy="no-referrer"
          className="size-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      className="size-9 rounded-full flex items-center justify-center text-white font-semibold text-body-sm"
      style={{ backgroundColor: color }}
    >
      {initialsFromName(displayName)}
    </div>
  );
}

function PromptCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 px-4 py-4 flex items-start gap-3">
      <div className="size-9 shrink-0 rounded-xl bg-white text-ink-700 ring-1 ring-ink-200 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink-900">{title}</div>
        <p className="mt-1 text-caption text-ink-500">{body}</p>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="h-14 rounded-xl bg-ink-100/70 animate-pulse" />
      <div className="h-10 rounded-xl bg-ink-100/70 animate-pulse" />
      <div className="h-10 rounded-xl bg-ink-100/70 animate-pulse" />
    </div>
  );
}

function asMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : "Something went wrong.";
}
